import { useEffect, useRef, useState } from 'react';
import { Lightbulb, MessageCircle, Paperclip, Send, CheckCircle2, X } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useAuth } from '@/contexts/AuthContext';
import { submitSentryFeedback } from '@/lib/sentry';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type FeedbackType = 'support' | 'idea';

interface CleviaFeedbackModalProps {
  type: FeedbackType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CleviaFeedbackModal({ type, open, onOpenChange }: CleviaFeedbackModalProps) {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<FeedbackType>(type);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setActiveTab(type);
      setSubject('');
      setMessage('');
      setAttachment(null);
      setAttachmentError('');
      setSent(false);
    }
  }, [open, type]);

  const isIdea = activeTab === 'idea';
  const canSend = subject.trim().length > 0 && message.trim().length > 0 && !isSending;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSend) return;

    setIsSending(true);
    try {
      await submitSentryFeedback({
        type: activeTab,
        subject: subject.trim(),
        message: message.trim(),
        email: user?.email || undefined,
        currency,
        attachment,
      });
      setSent(true);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-[520px] gap-0 overflow-hidden rounded-2xl border-gray-200 bg-white p-0 shadow-2xl dark:border-gray-700 dark:bg-[#111318]">
        <DialogHeader className="border-b border-gray-100 px-6 pb-5 pt-6 text-left dark:border-gray-800">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#16c47f]/10 text-[#16c47f]">
              {isIdea ? <Lightbulb className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
            </span>
            {t('feedback.title' as any)}
          </DialogTitle>
          <DialogDescription className="pl-11 text-sm leading-relaxed">
            {t('feedback.description' as any)}
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="px-6 py-12 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#16c47f]/10 text-[#16c47f]">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('feedback.successTitle' as any)}
            </h3>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500 dark:text-gray-400">
              {t('feedback.successDescription' as any)}
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="mt-6 bg-[#16c47f] text-white hover:bg-[#12b070]"
            >
              {t('feedback.close' as any)}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mx-6 mt-5 grid grid-cols-2 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
              {(['support', 'idea'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold transition-all ${
                    activeTab === tab
                      ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  {tab === 'support' ? (
                    <MessageCircle className="h-4 w-4" />
                  ) : (
                    <Lightbulb className="h-4 w-4" />
                  )}
                  {t(`feedback.tabs.${tab}` as any)}
                </button>
              ))}
            </div>

            <div className="space-y-4 px-6 py-5">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t(`feedback.helper.${activeTab}` as any)}
              </p>
              <label className="block space-y-1.5">
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  {t('feedback.subject' as any)}
                </span>
                <input
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  placeholder={t(`feedback.subjectPlaceholder.${activeTab}` as any)}
                  className="h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 text-sm text-gray-900 outline-none transition focus:border-[#16c47f] focus:ring-2 focus:ring-[#16c47f]/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  maxLength={120}
                  required
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  {t('feedback.message' as any)}
                </span>
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder={t(`feedback.messagePlaceholder.${activeTab}` as any)}
                  className="min-h-32 w-full resize-y rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3 text-sm text-gray-900 outline-none transition focus:border-[#16c47f] focus:ring-2 focus:ring-[#16c47f]/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  maxLength={4000}
                  required
                />
              </label>
              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.txt,.log"
                  className="sr-only"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) {
                      setAttachment(null);
                      setAttachmentError(t('feedback.attachmentTooLarge' as any));
                      event.target.value = '';
                      return;
                    }
                    setAttachment(file);
                    setAttachmentError('');
                  }}
                />
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="gap-2"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip className="h-4 w-4" />
                    {t('feedback.addAttachment' as any)}
                  </Button>
                  {attachment && (
                    <button
                      type="button"
                      onClick={() => {
                        setAttachment(null);
                        setAttachmentError('');
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      className="flex min-w-0 items-center gap-1 text-sm text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                      title={t('feedback.removeAttachment' as any)}
                    >
                      <span className="max-w-[180px] truncate">{attachment.name}</span>
                      <X className="h-4 w-4 shrink-0" />
                    </button>
                  )}
                </div>
                {attachmentError && <p className="text-sm text-red-500">{attachmentError}</p>}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4 dark:border-gray-800">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('feedback.cancel' as any)}
              </Button>
              <Button
                type="submit"
                disabled={!canSend}
                className="gap-2 bg-[#16c47f] text-white hover:bg-[#12b070] disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {isSending
                  ? t('feedback.sending' as any)
                  : t(`feedback.submit.${activeTab}` as any)}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
